import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, AlertCircle, Phone } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface EmailOnlyStepProps {
  onEmailSubmit: (email: string) => void;
  onGoogleAuth: () => void;
  onPhoneAuth: () => void;
  isLoading: boolean;
}

export const EmailOnlyStep: React.FC<EmailOnlyStepProps> = ({
  onEmailSubmit,
  onGoogleAuth,
  onPhoneAuth,
  isLoading
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      setEmailError('');
      setIsEmailValid(false);
      return;
    }
    
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      setIsEmailValid(false);
    } else {
      setEmailError('');
      setIsEmailValid(true);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    validateEmail(newEmail);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailValid && !isLoading) {
      onEmailSubmit(email);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium">
          Email address <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={handleEmailChange}
            className={`pr-10 ${
              email && (isEmailValid ? 'border-green-500 focus:ring-green-500' : emailError ? 'border-red-500 focus:ring-red-500' : '')
            }`}
            required
            autoFocus
            aria-describedby={emailError ? 'email-error' : isEmailValid ? 'email-success' : 'email-help'}
          />
          {email && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {isEmailValid ? (
                <Check className="h-4 w-4 text-green-500" aria-hidden="true" />
              ) : emailError ? (
                <AlertCircle className="h-4 w-4 text-red-500" aria-hidden="true" />
              ) : null}
            </div>
          )}
        </div>
        {emailError && (
          <p id="email-error" className="text-sm text-red-600 flex items-center gap-1" role="alert" aria-live="polite">
            <AlertCircle className="h-3 w-3" />
            {emailError}
          </p>
        )}
        {isEmailValid && (
          <p id="email-success" className="text-sm text-green-600 flex items-center gap-1" role="status" aria-live="polite">
            <Check className="h-3 w-3" />
            Looks good!
          </p>
        )}
      </div>

      <div className="space-y-4">
        <Button 
          type="submit" 
          className="w-full" 
          disabled={!isEmailValid || isLoading}
        >
          {isLoading ? 'Sending verification email...' : 'Continue'}
        </Button>
        
        <p id="email-help" className="text-sm text-muted-foreground text-center">
          We'll verify your email next.
        </p>
      </div>

      {/* Divider */}
      <div className="flex items-center my-6">
        <Separator className="flex-1" />
        <span className="px-3 text-sm text-muted-foreground">Or continue with</span>
        <Separator className="flex-1" />
      </div>

      {/* Alternative authentication methods */}
      <div className="space-y-3">
        <Button 
          type="button"
          variant="outline" 
          className="w-full"
          onClick={onGoogleAuth}
          disabled={isLoading}
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>
        
        <Button 
          type="button"
          variant="outline" 
          className="w-full"
          onClick={onPhoneAuth}
          disabled={isLoading}
        >
          <Phone className="w-4 h-4 mr-2" />
          Sign in with Phone Number
        </Button>
      </div>
    </form>
  );
};